// Validates each accepted message and hands it directly to the harness. The
// harness owns optimistic presentation and delivery.
import { ArrowUp, Loader2, Mic, Paperclip } from 'lucide-react';
import {
    type ChangeEvent,
    type ClipboardEvent,
    type Dispatch,
    type FormEvent,
    Fragment,
    type KeyboardEvent,
    type ReactNode,
    type SetStateAction,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { Textarea } from '@apps/web/components/ui/textarea';
import {
    EntityReferenceLabelsProvider,
    type EntityReferenceScope,
    useHasEntityReferenceStore,
} from '@apps/web/features/phoebe_chat_v3/api/entity_references';
import { type MentionCandidate } from '@apps/web/features/phoebe_chat_v3/api/reference_search';
import { canSubmitComposerValue } from '@apps/web/features/phoebe_chat_v3/model/composer_draft';
import {
    draftIssue,
    draftIssueCopy,
    MESSAGE_CHARACTER_LIMIT,
} from '@apps/web/features/phoebe_chat_v3/model/draft_rules';
import {
    insertMention,
    mentionQueryAt,
    type MentionRange,
    type MentionSegment,
    mentionSegments,
    type MentionToken,
    uniqueMentionLabel,
    withMentionTokens,
} from '@apps/web/features/phoebe_chat_v3/model/mentions';
import {
    messageClipboardToDraft,
    recallCopiedMessage,
} from '@apps/web/features/phoebe_chat_v3/model/message_clipboard';
import { type ReferenceType } from '@apps/web/features/phoebe_chat_v3/model/reference_tokens';
import { type ComposerFeature } from '@apps/web/features/phoebe_chat_v3/profiles/agent_profile';
import { CHAT_COLUMN_CLASS } from '@apps/web/features/phoebe_chat_v3/ui/chat_column';
import { ChatHomePresetRows } from '@apps/web/features/phoebe_chat_v3/ui/chat_home';
import { MentionSearch } from '@apps/web/features/phoebe_chat_v3/ui/mention_search';
import { ComposerAttachmentCards } from '@apps/web/features/phoebe_chat_v3/ui/primitives/composer_attachments';
import { MentionPicker } from '@apps/web/features/phoebe_chat_v3/ui/primitives/mention_picker';
import {
    REFERENCE_TYPE_ICONS,
    REFERENCE_TYPE_TINT_CLASSES,
} from '@apps/web/features/phoebe_chat_v3/ui/reference_icons';
import { useShiftReferenceChipClass } from '@apps/web/features/phoebe_chat_v3/ui/shift_reference_palette';
import { type UseRunUploadAttachmentsResult } from '@apps/web/hooks/use_run_upload_attachments';
import { useVoiceInput } from '@apps/web/hooks/use_voice_input';
import { cn } from '@apps/web/lib/utils';

type TextComposerFeature = Extract<ComposerFeature, { readonly kind: 'text' }>;

export type ComposerVoiceInput = Readonly<{
    readonly flow: string;
    readonly organizationId: string;
}>;

/** Where "@" mentions search; absent means mentions are off. */
export type ComposerReferenceSearch = Readonly<{
    readonly organizationId: string;
    readonly organizationTimeZone: string;
    readonly mode: 'live' | 'sandbox';
    readonly outreachId?: string;
}>;

export type ComposerPrefill = Readonly<{
    readonly text: string;
    readonly onDelivered: () => void;
}>;

export type ComposerProps = Readonly<{
    readonly disabled?: boolean;
    readonly disabledReasonId?: string;
    readonly displayName: string;
    readonly feature: TextComposerFeature;
    /** `flush` drops the standalone reading-column frame for nested use. */
    readonly layout?: 'column' | 'flush';
    readonly onSend: (
        content: string,
        activityEventIds: ReadonlyArray<string>,
        uploadIds: ReadonlyArray<string>,
    ) => Promise<boolean> | null;
    /** Overrides the feature placeholder, e.g. once a conversation exists. */
    readonly placeholder?: string;
    readonly referenceSearch?: ComposerReferenceSearch;
    /** Shows the feature's preset rows resting on the composer card. */
    readonly presetsVisible?: boolean;
    /** Harness-provided chrome for the card's foot, left of the send button. */
    readonly controls?: ReactNode;
    /** Selected records included with the next message. */
    readonly contextAttachments?: ReactNode;
    /** Sits immediately before the send button, e.g. the stop control. */
    readonly trailingControls?: ReactNode;
    /** Enables the shared record-and-transcribe input in the mic slot. */
    readonly voiceInput?: ComposerVoiceInput;
    /** A newly-created run's first draft, accepted once. */
    readonly initialMessage?: Readonly<{
        readonly activityEventIds?: ReadonlyArray<string>;
        readonly id: string;
        readonly content: string;
        /** Called once the backend accepts the first draft. */
        readonly onQueued?: () => void;
    }>;
    /** An external draft request for this run's existing composer. */
    readonly prefill?: ComposerPrefill;
    /** Enables file and image attachments; absent means uploads are off. */
    readonly uploads?: UseRunUploadAttachmentsResult;
    /** Restores the typed draft when the send resolves unaccepted. For
        sends with no optimistic transcript row carrying the failed text
        (a docked question answer), losing the answer loses the draft. */
    readonly restoreDraftOnSendFailure?: boolean;
    /** Host-owned draft state. A host that swaps between composer
        instances (the docked question) shares one pair so the typed
        draft — and a late failure restore — survives the swap. */
    readonly draftState?: readonly [string, Dispatch<SetStateAction<string>>];
    /** Fired on draft edits from user input (typing, mention picks, voice
        transcription) — not on prefill or the post-send clear — so hosts
        can broadcast typing presence to teammates in the same chat. */
    readonly onTyping?: () => void;
    /** Focus the textarea when this becomes true, e.g. opening a chat. */
    readonly autoFocus?: boolean;
}>;

export type ComposerCardProps = Readonly<{
    readonly canSubmit: boolean;
    readonly controls?: ReactNode;
    readonly contextAttachments?: ReactNode;
    readonly disabled?: boolean;
    readonly disabledReasonId?: string;
    readonly displayName: string;
    readonly failureCopy?: string | null;
    readonly feature: TextComposerFeature;
    /** `flush` drops the standalone reading-column frame for nested use. */
    readonly layout?: 'column' | 'flush';
    /** `display` is the draft as typed, for showing before the run exists. */
    readonly onDeliver: (message: string, display: string) => void;
    readonly onValueChange: (value: string) => void;
    readonly placeholder?: string;
    readonly presetsVisible?: boolean;
    readonly referenceSearch?: ComposerReferenceSearch;
    readonly trailingControls?: ReactNode;
    readonly uploads?: UseRunUploadAttachmentsResult;
    readonly value: string;
    readonly voiceInput?: ComposerVoiceInput;
    readonly autoFocus?: boolean;
}>;

export function Composer({
    disabled = false,
    disabledReasonId,
    displayName,
    feature,
    layout = 'column',
    onSend,
    placeholder,
    presetsVisible = false,
    referenceSearch,
    controls,
    contextAttachments,
    trailingControls,
    voiceInput,
    initialMessage,
    prefill,
    uploads,
    restoreDraftOnSendFailure = false,
    draftState,
    onTyping,
    autoFocus = false,
}: ComposerProps): ReactNode {
    const localDraftState = useState('');
    const [draft, setDraft] = draftState ?? localDraftState;
    const canSubmit = canSubmitComposerValue(draft, {
        hasReadyAttachment: uploads?.hasReadyAttachment,
    });

    const deliver = useCallback(
        (
            message: string,
            activityEventIds: ReadonlyArray<string> = [],
            display: string = message,
        ): Promise<boolean> | null => {
            if (disabled) {
                return null;
            }
            const content = message.trim();
            // Only finished uploads ride the message; one still in flight
            // stays staged and can be included in the next message.
            const uploadIds = uploads?.readyUploadIds ?? [];
            if (
                !canSubmitComposerValue(content, {
                    hasReadyAttachment: uploadIds.length > 0,
                })
            ) {
                return null;
            }
            const accepted = onSend(content, activityEventIds, uploadIds);
            // Local rejection has no optimistic message to carry the draft.
            if (accepted === null) {
                return null;
            }
            setDraft('');
            void accepted.then((wasAccepted) => {
                if (wasAccepted) {
                    uploads?.clearAttachmentsByUploadIds(uploadIds);
                    return;
                }
                if (restoreDraftOnSendFailure) {
                    // Text the user typed while the send was in flight wins
                    // over the restored draft.
                    setDraft((current) => (current === '' ? display : current));
                }
            });
            return accepted;
        },
        [disabled, onSend, restoreDraftOnSendFailure, setDraft, uploads],
    );

    useEffect(() => {
        if (initialMessage === undefined) {
            return;
        }
        const storageKey = `phoebe:v3-initial-message:${initialMessage.id}`;
        if (window.sessionStorage.getItem(storageKey) === 'queued') {
            return;
        }
        const accepted = deliver(
            initialMessage.content,
            initialMessage.activityEventIds ?? [],
        );
        if (accepted === null) {
            return;
        }
        window.sessionStorage.setItem(storageKey, 'queued');
        void accepted.then((wasAccepted) => {
            if (wasAccepted) {
                initialMessage.onQueued?.();
            }
        });
    }, [deliver, initialMessage]);

    useEffect(() => {
        if (prefill === undefined) {
            return;
        }
        setDraft(prefill.text);
        prefill.onDelivered();
    }, [prefill, setDraft]);

    return (
        <ComposerCard
            canSubmit={canSubmit}
            controls={controls}
            contextAttachments={contextAttachments}
            disabled={disabled}
            disabledReasonId={disabledReasonId}
            displayName={displayName}
            feature={feature}
            layout={layout}
            onDeliver={(message, display) => deliver(message, [], display)}
            onValueChange={(value) => {
                onTyping?.();
                setDraft(value);
            }}
            placeholder={placeholder}
            presetsVisible={presetsVisible}
            referenceSearch={referenceSearch}
            trailingControls={trailingControls}
            uploads={uploads}
            value={draft}
            voiceInput={voiceInput}
            autoFocus={autoFocus}
        />
    );
}

/**
 * The entity icon a mention chip shows in the draft mirror. It is drawn over
 * the hidden "@" slot, so it sits within the slot's width and cannot overlap
 * the label or move the caret.
 */
function MentionIcon({ type }: { readonly type: ReferenceType }): ReactNode {
    const Icon = REFERENCE_TYPE_ICONS[type];
    return (
        <Icon
            aria-hidden="true"
            // Centred on the slot's line box, exactly like the sent
            // badge centres its icon on its own line box: measured, that
            // puts the icon's ink centre within 0.1px of the surrounding
            // caps' centre and its base on the text baseline.
            // 0.875em inside the ~0.915em "@" slot, pulled left onto the chip's
            // painted padding, leaves the widest icon (the calendar) a
            // visible gap before the name instead of touching it.
            // No color class: the stroke's currentColor inherits the chip
            // tint's text color (the transparent "@" is a sibling span).
            className="absolute top-1/2 -left-[2px] size-[0.875em] -translate-y-1/2"
        />
    );
}

/**
 * One badge in the draft mirror. The "@" stays in the draft for caret
 * alignment; the icon draws over it. Padding beyond the negative margins
 * paints the chip over the neighbouring space without moving text. Facing
 * chips keep a smaller overhang so they do not overlap.
 */
function DraftMentionChip({
    segment,
    referenceScope,
    trimStart = false,
    trimEnd = false,
}: {
    readonly segment: Extract<MentionSegment, { kind: 'mention' }>;
    readonly referenceScope?: EntityReferenceScope;
    readonly trimStart?: boolean;
    readonly trimEnd?: boolean;
}): ReactNode {
    const shiftChipClass = useShiftReferenceChipClass(
        segment.type,
        segment.id,
        referenceScope,
    );
    const label = segment.text.slice(1);
    const overhangLeft = trimStart ? 1 : 2;
    const overhangRight = trimEnd ? 1 : 2;
    return (
        <span
            className={cn(
                'relative rounded-md py-[2px]',
                shiftChipClass ?? REFERENCE_TYPE_TINT_CLASSES[segment.type],
            )}
            style={{
                paddingLeft: overhangLeft,
                marginLeft: -overhangLeft,
                paddingRight: overhangRight,
                marginRight: -overhangRight,
            }}
        >
            {/* Only the "@" glyph goes transparent; the icon drawn over it
                inherits the chip tint's text color via currentColor, so a
                status-colored shift chip keeps one hue. */}
            <span className="relative inline-block">
                <MentionIcon type={segment.type} />
                <span className="text-transparent">@</span>
            </span>
            <span>{label}</span>
        </span>
    );
}

export function ComposerCard({
    canSubmit,
    controls,
    contextAttachments,
    disabled = false,
    disabledReasonId,
    displayName,
    failureCopy = null,
    feature,
    layout = 'column',
    onDeliver,
    onValueChange,
    placeholder,
    presetsVisible = false,
    referenceSearch,
    trailingControls,
    uploads,
    value,
    voiceInput,
    autoFocus = false,
}: ComposerCardProps): ReactNode {
    const content = value.trim();
    const presets = feature.presets ?? [];
    // Shift mention chips resolve their calendar status through the entity
    // reference store. Inside a run harness the transcript's store serves
    // them; a host without one (the sidebar's new-chat composer) gets a
    // store mounted below, scoped from the same place mentions search.
    const hasReferenceStore = useHasEntityReferenceStore();
    const referenceScope = useMemo<EntityReferenceScope | undefined>(
        () =>
            referenceSearch === undefined
                ? undefined
                : {
                      organizationId: referenceSearch.organizationId,
                      mode: referenceSearch.mode,
                  },
        [referenceSearch],
    );
    const fileInputRef = useRef<HTMLInputElement>(null);
    const hasResponseControl =
        trailingControls !== undefined &&
        trailingControls !== null &&
        trailingControls !== false;
    const voice = useVoiceInput({
        organizationId: voiceInput?.organizationId ?? '',
        currentText: value,
        onTranscribed: onValueChange,
        composer: 'v3_chat_composer',
        voiceInputFlow: voiceInput?.flow ?? 'agent_chat',
        disabled: disabled || voiceInput === undefined || hasResponseControl,
    });
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const backdropRef = useRef<HTMLDivElement>(null);
    const autoFocusWasEnabledRef = useRef(false);
    useLayoutEffect(() => {
        const becameEnabled = autoFocus && !autoFocusWasEnabledRef.current;
        autoFocusWasEnabledRef.current = autoFocus;
        if (!becameEnabled || disabled) {
            return;
        }
        textareaRef.current?.focus();
    }, [autoFocus, disabled]);
    const resizeComposer = useCallback(() => {
        const textarea = textareaRef.current;
        if (textarea === null) {
            return;
        }
        // Older Safari and Firefox ignore field-sizing: content.
        const scrollTop = textarea.scrollTop;
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
        textarea.scrollTop = scrollTop;
        if (backdropRef.current !== null) {
            // Native scrollbars can consume width on Windows and macOS.
            // +8 covers the mirror's 4px bleed per side, which keeps a
            // chip's painted overhang visible at the draft's edges.
            backdropRef.current.style.width = `${textarea.clientWidth + 8}px`;
            backdropRef.current.scrollTop = textarea.scrollTop;
        }
    }, []);
    useLayoutEffect(resizeComposer, [resizeComposer, value]);
    useLayoutEffect(() => {
        const textarea = textareaRef.current;
        if (textarea === null) {
            return;
        }
        let width = textarea.getBoundingClientRect().width;
        const observer = new ResizeObserver(() => {
            const nextWidth = textarea.getBoundingClientRect().width;
            if (nextWidth !== width) {
                width = nextWidth;
                resizeComposer();
            }
        });
        observer.observe(textarea);
        window.addEventListener('resize', resizeComposer);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', resizeComposer);
        };
    }, [resizeComposer]);
    const [mention, setMention] = useState<MentionRange | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [mentions, setMentions] = useState<ReadonlyArray<MentionToken>>([]);
    // Tokens are longer than the names they replace, so the limit is checked
    // on the message that actually ships.
    const issue = draftIssue(withMentionTokens(content, mentions), {
        limit: MESSAGE_CHARACTER_LIMIT,
        required: uploads?.hasReadyAttachment !== true,
    });
    const submitEnabled =
        canSubmit &&
        issue === null &&
        !voice.isRecording &&
        !voice.isTranscribing;
    const visibleFailureCopy =
        failureCopy ??
        voice.voiceInputError ??
        uploads?.errorMessage ??
        (issue?.kind === 'too_long' ? draftIssueCopy(issue, 'Message') : null);
    // Escape or a selected result ends this occurrence, so later words stay
    // message text. A successful empty search only dismisses its exact query
    // because another word may turn the partial name into a match.
    const dismissedRef = useRef<
        (MentionRange & { readonly suppressLongerQuery?: boolean }) | null
    >(null);
    const [pendingCaret, setPendingCaret] = useState<number | null>(null);
    // The caret can only move once React has written the new value into the
    // textarea, so the move waits for that commit.
    useLayoutEffect(() => {
        const element = textareaRef.current;
        if (pendingCaret === null || element === null) {
            return;
        }
        element.focus();
        element.setSelectionRange(pendingCaret, pendingCaret);
        setPendingCaret(null);
    }, [pendingCaret, value]);
    const mentionsEnabled = referenceSearch !== undefined;
    const pickerOpen = mentionsEnabled && mention !== null;
    const [candidates, setCandidates] = useState<
        ReadonlyArray<MentionCandidate>
    >([]);
    const [searching, setSearching] = useState(false);
    const [shiftsTruncated, setShiftsTruncated] = useState(false);
    const receiveResults = useCallback(
        (
            results: ReadonlyArray<MentionCandidate>,
            loading: boolean,
            outreachShiftsTruncated: boolean,
        ) => {
            setCandidates(results);
            setSearching(loading);
            setShiftsTruncated(outreachShiftsTruncated);
        },
        [],
    );
    const closeSettledEmptyMention = useCallback(() => {
        setMention((current) => {
            dismissedRef.current = current;
            return null;
        });
    }, []);
    function trackMention(element: HTMLTextAreaElement): void {
        if (!mentionsEnabled) {
            return;
        }
        const next = mentionQueryAt(element.value, element.selectionStart);
        const dismissed = dismissedRef.current;
        if (
            next !== null &&
            dismissed !== null &&
            dismissed.start === next.start &&
            (dismissed.query === next.query ||
                (dismissed.suppressLongerQuery === true &&
                    next.query.startsWith(`${dismissed.query} `)))
        ) {
            return;
        }
        dismissedRef.current = null;
        setMention(next);
        if (next === null || next.query !== mention?.query) {
            setActiveIndex(0);
        }
    }

    function mentionsStillIn(
        text: string,
        current: ReadonlyArray<MentionToken>,
    ): ReadonlyArray<MentionToken> {
        return current.filter((item) => text.includes(`@${item.label}`));
    }

    function handleChange(event: ChangeEvent<HTMLTextAreaElement>): void {
        const next = event.currentTarget.value;
        onValueChange(next);
        // A pick belongs to the draft it was made in. Typing prunes picks
        // whose name is gone, so a name typed by hand in a later draft
        // stays plain text; a host restoring a failed draft keeps them.
        setMentions((current) => mentionsStillIn(next, current));
        trackMention(event.currentTarget);
    }

    function selectCandidate(candidate: MentionCandidate): void {
        if (mention === null) {
            return;
        }
        const label = uniqueMentionLabel(
            candidate.label,
            candidate.detail,
            mentions,
            candidate.token,
        );
        const inserted = insertMention(value, mention, label);
        onValueChange(inserted.text);
        setMentions((current) => [
            ...current.filter((item) => item.label !== label),
            { label, token: candidate.token, type: candidate.type },
        ]);
        // The inserted trailing space ends this occurrence even though the
        // parser now allows spaces inside names. A later "@" still starts a
        // fresh occurrence at a different index.
        dismissedRef.current = {
            start: mention.start,
            end: inserted.caret - 1,
            query: label,
            suppressLongerQuery: true,
        };
        setMention(null);
        setPendingCaret(inserted.caret);
    }

    function pasteMessageClipboard(
        event: ClipboardEvent<HTMLTextAreaElement>,
    ): void {
        if (referenceSearch === undefined) {
            return;
        }
        const payload = recallCopiedMessage(
            event.clipboardData.getData('text/plain'),
            {
                organizationId: referenceSearch.organizationId,
                mode: referenceSearch.mode,
            },
        );
        if (payload === null) {
            return;
        }
        event.preventDefault();
        const { selectionStart, selectionEnd } = event.currentTarget;
        const surviving = mentionsStillIn(
            value.slice(0, selectionStart) + value.slice(selectionEnd),
            mentions,
        );
        const draft = messageClipboardToDraft(payload, surviving);
        const nextValue =
            value.slice(0, selectionStart) +
            draft.text +
            value.slice(selectionEnd);
        const caret = selectionStart + draft.text.length;
        onValueChange(nextValue);
        setMentions([...surviving, ...draft.mentions]);
        const trailingMention = mentionQueryAt(nextValue, caret);
        dismissedRef.current =
            trailingMention === null
                ? null
                : { ...trailingMention, suppressLongerQuery: true };
        setMention(null);
        setPendingCaret(caret);
    }

    function submit(event: FormEvent<HTMLFormElement>): void {
        event.preventDefault();
        if (disabled || !submitEnabled) {
            return;
        }
        const message = withMentionTokens(content, mentions);
        voice.discardPendingTranscription();
        // Picked mentions stay known after a send: a host that restores the
        // typed draft after a failure can re-send the same tokens, and a
        // label that no longer appears in the text replaces nothing.
        onDeliver(message, content);
        setMention(null);
    }

    function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
        if (event.nativeEvent.isComposing) {
            return;
        }
        if (pickerOpen) {
            const count = candidates.length;
            if (event.key === 'Escape') {
                event.preventDefault();
                dismissedRef.current = {
                    ...mention,
                    suppressLongerQuery: true,
                };
                setMention(null);
                return;
            }
            if (event.key === 'ArrowDown' && count > 0) {
                event.preventDefault();
                setActiveIndex((index) => (index + 1) % count);
                return;
            }
            if (event.key === 'ArrowUp' && count > 0) {
                event.preventDefault();
                setActiveIndex((index) => (index - 1 + count) % count);
                return;
            }
            if ((event.key === 'Enter' || event.key === 'Tab') && count > 0) {
                event.preventDefault();
                const candidate = candidates[activeIndex];
                if (candidate !== undefined) {
                    selectCandidate(candidate);
                }
                return;
            }
            if ((event.key === 'Enter' || event.key === 'Tab') && searching) {
                event.preventDefault();
                return;
            }
        }
        if (event.key !== 'Enter' || event.shiftKey) {
            return;
        }
        event.preventDefault();
        event.currentTarget.form?.requestSubmit();
    }

    const card = (
        <form
            aria-label={`${displayName} message composer`}
            className={cn(
                '@container/v3-composer overflow-visible',
                layout === 'column' ? CHAT_COLUMN_CLASS : 'min-w-0',
            )}
            onSubmit={submit}
        >
            {presetsVisible && presets.length > 0 && (
                <ChatHomePresetRows
                    disabled={disabled}
                    presets={presets}
                    onSelect={(preset) => onDeliver(preset.label, preset.label)}
                />
            )}
            <div className="relative">
                {pickerOpen && referenceSearch !== undefined && (
                    <MentionSearch
                        organizationId={referenceSearch.organizationId}
                        organizationTimeZone={
                            referenceSearch.organizationTimeZone
                        }
                        mode={referenceSearch.mode}
                        outreachId={referenceSearch.outreachId}
                        query={mention.query}
                        onResults={receiveResults}
                        onSettledNoMatches={closeSettledEmptyMention}
                    />
                )}
                {pickerOpen && (
                    <MentionPicker
                        candidates={candidates}
                        activeIndex={activeIndex}
                        loading={searching}
                        outreachShiftsTruncated={shiftsTruncated}
                        onActivate={setActiveIndex}
                        onSelect={selectCandidate}
                        referenceScope={referenceScope}
                    />
                )}
                {/* Attached context sits as a quiet band tucked behind the
                    input card, one cut-off line (the records remain visible
                    elsewhere on the page). empty:hidden collapses the band
                    when every attachment component renders null. */}
                {contextAttachments !== undefined && (
                    <div className="bg-muted/60 relative -mb-3 rounded-t-lg px-3.5 pt-2 pb-5 empty:hidden">
                        {contextAttachments}
                    </div>
                )}
                <div className="bg-card relative rounded-lg border px-4 pt-3.5 pb-2.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                    {uploads !== undefined && (
                        <ComposerAttachmentCards
                            attachments={uploads.attachments}
                            disabled={disabled}
                            onRemove={uploads.removeAttachment}
                        />
                    )}
                    <div className="relative">
                        {/* A textarea cannot style part of its text, so a mirror
                        layer draws the draft behind it with mention badges,
                        and the textarea keeps only its caret and selection
                        visible. */}
                        {mentionsEnabled && value !== '' && (
                            <div
                                ref={backdropRef}
                                aria-hidden="true"
                                // The 4px bleed (-inset-x-1 cancelled by px-1) keeps
                                // a chip's painted overhang from clipping at
                                // the overflow-hidden edge when a mention
                                // starts the draft or a wrapped line.
                                className="text-foreground text-v3-chat pointer-events-none absolute -inset-x-1 inset-y-0 max-h-[40svh] overflow-hidden px-1 break-words whitespace-pre-wrap"
                            >
                                {(() => {
                                    const segments = mentionSegments(
                                        value,
                                        mentions,
                                    );
                                    // Two chips face each other when only a
                                    // single space separates their mentions.
                                    const chipsFace = (
                                        betweenIndex: number,
                                    ): boolean => {
                                        const between = segments[betweenIndex];
                                        return (
                                            between !== undefined &&
                                            between.kind === 'text' &&
                                            between.text === ' ' &&
                                            segments[betweenIndex - 1]?.kind ===
                                                'mention' &&
                                            segments[betweenIndex + 1]?.kind ===
                                                'mention'
                                        );
                                    };
                                    return segments.map((segment, index) =>
                                        segment.kind === 'mention' ? (
                                            <DraftMentionChip
                                                key={index}
                                                segment={segment}
                                                referenceScope={referenceScope}
                                                trimStart={chipsFace(index - 1)}
                                                trimEnd={chipsFace(index + 1)}
                                            />
                                        ) : (
                                            <Fragment key={index}>
                                                {segment.text}
                                            </Fragment>
                                        ),
                                    );
                                })()}
                                {value.endsWith('\n') ? '\u200b' : null}
                            </div>
                        )}
                        <Textarea
                            ref={textareaRef}
                            aria-label={`Message ${displayName}`}
                            aria-describedby={disabledReasonId}
                            disabled={disabled}
                            placeholder={placeholder ?? feature.placeholder}
                            value={value}
                            rows={1}
                            className={cn(
                                'scrollbar-none relative field-sizing-fixed max-h-[40svh] min-h-8 resize-none overflow-y-auto rounded-none border-0 bg-transparent p-0 text-v3-chat break-words shadow-none focus-visible:ring-0 md:text-v3-chat dark:bg-transparent',
                                mentionsEnabled &&
                                    'caret-foreground text-transparent',
                            )}
                            onScroll={(event) => {
                                if (backdropRef.current !== null) {
                                    backdropRef.current.scrollTop =
                                        event.currentTarget.scrollTop;
                                }
                            }}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                            onKeyUp={(event) =>
                                trackMention(event.currentTarget)
                            }
                            onClick={(event) =>
                                trackMention(event.currentTarget)
                            }
                            onPaste={(event) => {
                                const files = Array.from(
                                    event.clipboardData.files,
                                );
                                if (uploads !== undefined && files.length > 0) {
                                    event.preventDefault();
                                    uploads.onFilesSelected(files);
                                    return;
                                }
                                pasteMessageClipboard(event);
                            }}
                        />
                    </div>
                    <div className="mt-2.5 flex items-center justify-between gap-2">
                        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                            {/* Flex, not default block: the pills inside
                                would otherwise sit on a text baseline and
                                their differing font sizes offset them
                                vertically. */}
                            <fieldset
                                className="m-0 flex min-w-0 flex-wrap items-center gap-1.5 border-0 p-0"
                                disabled={disabled}
                            >
                                {controls}
                            </fieldset>
                            {visibleFailureCopy !== null && (
                                <span
                                    role="alert"
                                    className="text-muted-foreground text-v3-chat-meta min-w-0 [overflow-wrap:anywhere]"
                                >
                                    {visibleFailureCopy}
                                </span>
                            )}
                        </div>
                        <div className="flex shrink-0 items-center gap-1.5">
                            {uploads !== undefined && (
                                <Fragment>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple={true}
                                        accept={uploads.acceptedMimeTypes}
                                        tabIndex={-1}
                                        className="hidden"
                                        onChange={(event) => {
                                            const files = Array.from(
                                                event.currentTarget.files ?? [],
                                            );
                                            // Reset so re-picking the same
                                            // file fires another change.
                                            event.currentTarget.value = '';
                                            if (files.length > 0) {
                                                uploads.onFilesSelected(files);
                                            }
                                        }}
                                    />
                                    <button
                                        type="button"
                                        aria-label="Attach files"
                                        disabled={disabled}
                                        onClick={() =>
                                            fileInputRef.current?.click()
                                        }
                                        className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-agent-action grid size-[30px] shrink-0 place-items-center rounded-md transition-colors duration-150 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-default disabled:opacity-[0.42]"
                                    >
                                        <Paperclip
                                            aria-hidden="true"
                                            className="size-[17px]"
                                            strokeWidth={2.15}
                                        />
                                    </button>
                                </Fragment>
                            )}
                            {hasResponseControl
                                ? trailingControls
                                : voiceInput !== undefined &&
                                  voice.canRecordAudio && (
                                      <button
                                          type="button"
                                          aria-label={
                                              voice.isTranscribing
                                                  ? 'Transcribing voice message'
                                                  : voice.isRecording
                                                    ? 'Stop recording'
                                                    : 'Record voice message'
                                          }
                                          disabled={
                                              disabled || voice.isTranscribing
                                          }
                                          onClick={() => {
                                              if (voice.isRecording) {
                                                  voice.stopRecording();
                                                  return;
                                              }
                                              void voice.startRecording();
                                          }}
                                          className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-agent-action relative grid size-[30px] shrink-0 place-items-center rounded-md transition-colors duration-150 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-default disabled:opacity-[0.42]"
                                      >
                                          {voice.isTranscribing ? (
                                              <Loader2
                                                  aria-hidden="true"
                                                  className="size-[17px] animate-spin motion-reduce:animate-none"
                                              />
                                          ) : (
                                              <Mic
                                                  aria-hidden="true"
                                                  className="size-[18px]"
                                                  strokeWidth={2.15}
                                              />
                                          )}
                                          {voice.isRecording && (
                                              <span
                                                  aria-hidden="true"
                                                  className="absolute top-0.5 right-0.5 size-1.5 rounded-full bg-red-500"
                                              />
                                          )}
                                      </button>
                                  )}
                            <button
                                type="submit"
                                aria-label="Send message"
                                aria-describedby={disabledReasonId}
                                disabled={disabled || !submitEnabled}
                                className="bg-agent-action hover:bg-agent-action-strong disabled:bg-muted disabled:text-muted-foreground disabled:hover:bg-muted focus-visible:outline-agent-action grid size-[30px] shrink-0 place-items-center rounded-md text-white transition-[color,background-color,transform] duration-150 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 active:scale-[0.92] motion-reduce:transition-none motion-reduce:active:scale-100"
                            >
                                <ArrowUp
                                    aria-hidden="true"
                                    className="size-[15px]"
                                    strokeWidth={2.15}
                                />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    );
    if (!hasReferenceStore && referenceScope !== undefined) {
        return (
            <EntityReferenceLabelsProvider scope={referenceScope}>
                {card}
            </EntityReferenceLabelsProvider>
        );
    }
    return card;
}
